<template>
  <v-container fluid>
    <top-header
      title="Requête d'export"
      v-if="$route.path.startsWith('/backoffice')"
    />
    <core-app-bar v-else />
    <v-row>
      <v-col cols="2">
        <v-btn
          @click="goBack"
          :class="isAdmin ? '' : 'primary'"
          :color="isAdmin ? 'primary--text' : ''"
        >
          <v-icon>mdi-arrow-left-thin</v-icon>
          {{ $t('goBack') }}
        </v-btn>
      </v-col>
      <v-spacer />
      <v-tooltip bottom :disabled="!(edited || !exportName || $v.$invalid)">
        <template v-slot:activator="{ on, attrs }">
          <div v-on="on">
            <v-btn
              :class="{
                'mr-3': true,
                'primary--text':
                  isAdmin && !(edited || !exportName || $v.$invalid),
                'disable-button': edited || !exportName || $v.$invalid,
              }"
              :color="
                isAdmin && !(edited || !exportName || $v.$invalid)
                  ? ''
                  : 'primary'
              "
              @click="manualExport"
              :loading="downloadingExport"
              v-bind="attrs"
            >
              <v-icon>mdi-database-arrow-down-outline</v-icon>
              {{ $t('execute') }}
            </v-btn>
          </div>
        </template>
        <span>{{ $t('saveBeforeExport') }}</span>
      </v-tooltip>

      <v-tooltip
        bottom
        :disabled="!(!edited || !exportName || $v.$invalid || loadingExport)"
      >
        <template v-slot:activator="{ on, attrs }">
          <div v-on="on">
            <v-btn
              :class="{
                'mr-3': true,
                'primary--text':
                  isAdmin &&
                  !(!edited || !exportName || $v.$invalid || loadingExport),
                'disable-button':
                  !edited || !exportName || $v.$invalid || loadingExport,
              }"
              :color="
                isAdmin &&
                !(!edited || !exportName || $v.$invalid || loadingExport)
                  ? ''
                  : 'primary'
              "
              v-bind="attrs"
              @click="save"
            >
              <v-icon>mdi-content-save-outline</v-icon>
              {{ id ? $t('save') : $t('create') }}
            </v-btn>
          </div>
        </template>
        <span>{{
          $t(edited || $v.$invalid ? 'pleaseCheckInputErrors' : 'saved')
        }}</span>
      </v-tooltip>
    </v-row>

    <v-tabs class="mt-15" v-model="active_tab">
      <v-tab>{{ $t('informations') }}</v-tab>
      <v-tab :disabled="!selectedDataset">{{ $t('filtering') }}</v-tab>
      <v-tab :disabled="!id">{{ $t('history') }}</v-tab>

      <!-- Informations -->
      <v-tab-item class="mt-4 pa-4">
        <informations-tab
          :isAdmin="isAdmin"
          :rules="rules"
          :loadingExport="loadingExport"
          :loadingDatasets="loadingDatasets"
          :loadingMerchants="loadingMerchants"
          :merchants="merchants"
          :schedule="schedule"
          @changeSchedule="onChangeSchedule"
          :exportName="exportName"
          @changeName="onChangeName"
          :notifications="notificationList"
          @changeNotifications="onChangeNotifications"
          :selectedMerchant="selectedMerchant"
          @changeSelectedMerchant="onChangeSelectedMerchant"
          :selectedDataset="selectedDataset"
          @changeSelectedDataset="onChangeSelectedDataset"
        />
      </v-tab-item>

      <!-- Filtrage -->
      <v-tab-item class="mt-4 pa-4">
        <filter-tab
          :rules="rules"
          :loadingExport="loadingExport"
          :filterConditions="filterConditions"
          @changeFilterConditions="onChangeFilterConditions"
          :selectedColumns="selectedColumns"
          @changeSelectedColumns="onChangeSelectedColumns"
          :columns="columns"
        />
      </v-tab-item>

      <!-- Historique -->
      <v-tab-item class="transparent mt-4 ml-4">
        <history-tab
          @invalidated="onInvalidateHistory"
          :history="history"
          :loadingHistory="loadingHistory"
          :historyCount="historyCount"
          :historyOptions="historyOptions"
        />
      </v-tab-item>
    </v-tabs>
  </v-container>
</template>

<script>
import ExportsService from '@/services/ExportsService'
import DatasetService from '@/services/DatasetService'
import MerchantService from '@/services/MerchantService'
import { required, email, minLength, maxLength } from 'vuelidate/lib/validators'

export default {
  name: 'ExportDetails',
  components: {
    TopHeader: () => import('@/components/base/TopHeader'),
    CoreAppBar: () => import('@/components/layouts/core/AppBar'),
    InformationsTab: () => import('@/components/hmd/Exports/InformationsTab'),
    FilterTab: () => import('@/components/hmd/Exports/FilterTab'),
    HistoryTab: () => import('@/components/hmd/Exports/HistoryTab'),
  },
  data() {
    return {
      downloadingExport: false,
      loadingExport: false,
      loadingDatasets: false,
      loadingMerchants: false,
      loadingColumns: false,
      loadingHistory: true,
      schedule: {},
      notificationList: [],
      exportName: null,
      selectedDataset: null,
      selectedColumns: [],
      selectedMerchant: null,
      filterConditions: [],
      merchants: new Map(),
      history: [],
      historyCount: 0,
      columns: [],
      historyOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      edited: false,
      active_tab: 0,
      rules: {
        exportName: {
          required: (value) => required(value),
          minLength: (value) => minLength(2)(value),
          maxLength: (value) => maxLength(45)(value),
        },
        notificationList: {
          email: (arr) =>
            arr.every(email) && arr.every((email) => email.length < 75),
          maxLength: (arr) => arr.length <= 8,
        },
        selectedDataset: {
          required: (value) => required(value),
        },
        selectedColumns: {
          required: (value) => required(value),
          minLength: (value) => minLength(1)(value),
        },
        filterConditions: {
          minLength: (value) => minLength(1)(value),
          format: (arr) =>
            arr.every(
              ({ column, value, operator }) =>
                typeof column.value === 'number' &&
                value != null &&
                !!operator &&
                ((typeof value === 'object' &&
                  Object.values(value)
                    .filter((v) => typeof v === 'string')
                    .reduce((acc, v) => acc + v.length, 0) < 512) ||
                  typeof value !== 'string' ||
                  value.length < 512)
            ),
        },
      },
    }
  },
  validations() {
    return this.rules
  },
  props: {
    id: {
      type: String,
      default: null,
    },
    isAdmin: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    user() {
      return this.$store.state[
        this.$store.getters.isPreviewing ? 'preview' : 'user'
      ]
    },
  },
  mounted() {
    this.fetchMerchants()
    if (this.id) {
      this.fetchExport()
    } else {
      this.loadingExport = false
      this.loadingHistory = false
    }
    this.$v.$reset()
  },
  watch: {
    selectedMerchant() {
      this.fetchDatasets()
    },
    async selectedDataset() {
      await this.fetchColumns()
      const incompatible_dataset =
        this.selectedColumns.some(
          (col) => !this.columns.some((c) => c.value === col)
        ) ||
        this.filterConditions.some(
          (filter) => !this.columns.some((c) => c.value === filter.column.value)
        )

      if (incompatible_dataset) {
        this.selectedColumns = []
        this.filterConditions = this.filterConditions.map((filter) => ({
          ...filter,
          column: null,
        }))
      }
    },
    loadingExport(loading) {
      if (loading) {
        this.active_tab = 0
      }
    },
  },
  methods: {
    async fetchMerchants() {
      this.loadingMerchants = true
      if (this.isAdmin) {
        const merchants = await MerchantService.list(this, true)
        if (merchants.results) {
          for (const merchant of merchants.results) {
            this.merchants.set(merchant.id, {
              dataset: [],
              ...this.merchants.get(merchant.id),
              ...merchant,
            })
          }
        }
      } else {
        this.merchants.set(this.user.merchant.id, {
          dataset: [],
          ...this.user.merchant,
          ...this.merchants.get(this.user.merchant.id),
        })
        this.selectedMerchant = this.user.merchant.id
      }
      this.loadingMerchants = false
    },
    fetchDatasets() {
      this.loadingDatasets = true
      const merchantId = this.selectedMerchant || this.user.merchant.id
      MerchantService.getConsolidatedFiles(this, merchantId)
        .then((datasets) => {
          this.merchants[merchantId] = {
            ...this.merchants[merchantId],
            datasets: datasets.map((row_dataset) => {
              const d = {
                name: row_dataset.name,
                id: row_dataset.hmd_id,
              }
              let name = d.name
              if (d.name.startsWith('cat_')) {
                d.name = 'CAT results'
                return d
              }
              d.name = d.name.replace('consolidation_', '').split('_')[0]
              if (d.name == 'anomalies') {
                name = name.replace('consolidation_', '')
                name = name.replace('_', ' ')
                d.name = name
              }
              d.name = d.name.charAt(0).toUpperCase() + d.name.slice(1)
              return d
            }),
          }
        })
        .finally(() => {
          this.loadingDatasets = false
        })
    },
    async fetchColumns() {
      this.loadingColumns = true
      this.columns = []
      const dataset = (
        await DatasetService.get_informations(this, this.selectedDataset)
      ).data
      this.columns = dataset?.fields?.map((c) => ({
        text: c.name,
        value: c.id,
        type: c.type,
      }))
      this.loadingColumns = false
    },
    async fetchHistory() {
      this.loadingHistory = true
      const historyData = await ExportsService.listProjectFiles(
        this,
        this.historyOptions?.page,
        this.historyOptions?.itemsPerPage,
        this.id
      )
      this.history = historyData?.data?.results
      this.historyCount = historyData?.data?.count
      this.loadingHistory = false
    },
    async fetchExport() {
      this.loadingExport = true
      const export_ = await this.exportService('getExport', this.id)

      this.exportName = export_.name ?? ''
      this.notificationList = export_.notifications ?? []
      this.selectedDataset = export_.dataset?.hmd_id ?? null
      this.selectedMerchant = export_.dataset?.project_id ?? null
      this.selectedColumns = export_.columns?.map((c) => c.column.id) ?? []
      this.filterConditions =
        export_.filter_conditions?.map((filter) => ({
          ...filter,
          column: { text: filter.column.name, value: filter.column.id },
        })) ?? []
      this.schedule = export_.schedule ?? {}
      this.historyCount = export_.history_count

      this.loadingExport = false
    },
    async exportService(method, ...rest) {
      const data = (
        await ExportsService[method + (this.isAdmin ? 'Admin' : '')](
          this,
          ...rest
        )
      )?.data

      return data
    },
    onChangeSchedule(value, synonymous = false) {
      if (!synonymous) {
        this.edited = true
      }
      this.schedule = value
    },
    onChangeName(value) {
      this.edited = true
      this.exportName = value
    },
    onChangeNotifications(value) {
      this.edited = true
      this.notificationList = value
    },
    onChangeSelectedMerchant(value) {
      this.edited = true
      this.selectedMerchant = value
    },
    onChangeSelectedDataset(value) {
      this.edited = true
      this.selectedDataset = value
    },
    onChangeFilterConditions(value, synonymous = false) {
      if (!synonymous) {
        this.edited = true
      }
      this.filterConditions = value
    },
    onChangeSelectedColumns(value) {
      this.edited = true
      this.selectedColumns = value
    },
    onInvalidateHistory(historyOptions, historyCount) {
      this.historyOptions = historyOptions ?? this.historyOptions
      this.historyCount = historyCount ?? this.historyCount
      this.fetchHistory()
    },
    goBack() {
      this.$router.push({ name: this.isAdmin ? 'ExportsAdmin' : 'Exports' })
    },
    goToDetails(id) {
      this.$router.push({
        name: this.isAdmin ? 'ExportDetailsAdmin' : 'ExportDetails',
        params: { id },
      })
    },
    async manualExport() {
      this.downloadingExport = true
      await this.exportService('generateExport', this.id).finally(() => {
        this.downloadingExport = false
      })
      this.fetchHistory()
    },
    async save() {
      this.loadingExport = true
      // some component might format their props on focus out (ie combobox)
      await new Promise((resolve) => setTimeout(resolve, 0))

      const exportData = {
        name: this.exportName,
        notifications: this.notificationList,
        dataset: this.selectedDataset,
        columns: this.selectedColumns.map((col, i) => ({ id: col, order: i })),
        filter_conditions: this.filterConditions.map((filter) => ({
          ...filter,
          column: filter.column.value ?? filter.column.id,
        })),
        schedule: {
          ...this.schedule,
          live_at: this.schedule.live_at ? this.schedule.live_at : null,
          freq_choice: this.schedule.freq_choice ?? 'NONE',
        },
      }

      try {
        if (this.id) {
          await this.exportService('updateExport', this.id, exportData)
        } else {
          this.goToDetails(
            (await this.exportService('createExport', exportData)).id
          )
        }
        this.active_tab = 0
        this.edited = false
      } catch (error) {
        console.error(error)
      } finally {
        this.loadingExport = false
      }
    },
  },
}
</script>

<style>
.v-tabs {
  border-radius: 5px;
  overflow: hidden;
}
.v-tabs .v-tabs-bar {
  border-radius: initial;
}

/* voluntarily high specificity */
.v-application .v-btn.disable-button {
  pointer-events: none;
  cursor: default;
  background-color: #dadbdf !important;
  color: #a3a5ab !important;
}
</style>
